import { marked, Renderer } from 'marked'

// Markdown → HTML for the DB-backed blog. Post bodies are admin-authored and
// therefore trusted; we render them directly. (Comments are NOT markdown — they
// are rendered as escaped plain text in the component.)

export interface TocItem {
  id: string
  label: string
}

function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/`/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&[a-z]+;/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim()
}

/**
 * Render a Markdown post body to HTML plus a table of contents built from its
 * H2 headings. Headings get slug ids so the TOC can anchor-link to them, and
 * external links open in a new tab.
 */
export function renderMarkdown(md: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = []
  const seen = new Set<string>()
  const renderer = new Renderer()

  renderer.heading = (text, level) => {
    let id = slugify(stripTags(text)) || `section-${toc.length + 1}`
    while (seen.has(id)) id = `${id}-${toc.length + 1}`
    seen.add(id)
    if (level === 2) toc.push({ id, label: stripTags(text) })
    return `<h${level} id="${id}">${text}</h${level}>\n`
  }

  renderer.link = (href, title, text) => {
    const isExternal = /^https?:\/\//.test(href || '')
    const attrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''
    const t = title ? ` title="${title}"` : ''
    return `<a href="${href}"${t}${attrs}>${text}</a>`
  }

  const html = marked.parse(md, { gfm: true, breaks: false, renderer }) as string
  return { html, toc }
}
